import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useDelayedFlag } from '@/hooks/use-delayed-flag';
import { customersApi } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { StatusBadge } from './customer-status-badge';

interface Props {
  customerId: string;
}

/**
 * The customers whose parent is this one, as a section under the Details tab.
 *
 * A table, not a Details field: this is a collection, and squeezing it into a
 * definition list forced it into a comma-separated run that couldn't carry a QB
 * ID or a status. Not a tab either, though — there is nothing to manage here,
 * since the link is set from the child's own form.
 *
 * Renders nothing at all when there are no subsidiaries. Only parent companies
 * have any, so an empty section would be dead weight on the majority of
 * customer pages; a customer's lack of subsidiaries is already implied by the
 * Parent customer field above. A FAILED fetch is not the same thing and says so
 * — see the error branch below.
 */
const PAGE_SIZE = 25;

/** How long a fetch may run before it is worth telling the user about. Under
 *  this, a placeholder would appear and vanish faster than it could be read. */
const SKELETON_DELAY_MS = 300;

function SubsidiariesSection({ children }: { children: React.ReactNode }) {
  // `pt-4`, not `mt-4`: the Details tab wraps its children in `space-y-3`,
  // whose `> * ~ *` selector outranks a plain margin utility and would win.
  // Padding sits inside the section and simply adds to that gap, setting the
  // list apart from the field table above it.
  return <section className="space-y-2 pt-4">{children}</section>;
}

export function CustomerSubsidiariesPanel({ customerId }: Props) {
  const [page, setPage] = useState(0);

  // This component is NOT remounted when the route's customer changes — the
  // route is the same (`/customers/$id`), only the param moves — so `page`
  // would otherwise carry a deep page from one parent over to the next and
  // request an offset past the end of it.
  useEffect(() => {
    setPage(0);
  }, [customerId]);

  const query = useQuery({
    queryKey: queryKeys.customers.subsidiaries(customerId, page),
    queryFn: () =>
      customersApi.listSubsidiaries(customerId, {
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      }),
    // Keep the previous page on screen while the next one loads, so paging
    // doesn't collapse the section and jump the page around.
    placeholderData: keepPreviousData,
  });

  // `useDelayedFlag`, not `useDebouncedValue`: the latter seeds its state with
  // the value it is handed, so for a flag that starts true it fires on mount
  // and there is no delay at all.
  const loadingSettled = useDelayedFlag(query.isLoading, SKELETON_DELAY_MS);

  // A failure must NOT look like "no subsidiaries" (G15): collapsing the two
  // leaves a parent company's whole list silently missing with nothing to
  // report. This is the one state that renders even though the empty state
  // renders nothing.
  if (query.isError) {
    return (
      <SubsidiariesSection>
        <h3 className="text-sm font-medium text-muted-foreground">Subsidiaries</h3>
        <p className="rounded-md border border-destructive/50 p-3 text-sm text-destructive">
          Could not load subsidiaries. Reload the page to try again.
        </p>
      </SubsidiariesSection>
    );
  }

  // A skeleton, but only once loading has actually taken a moment. Rendering
  // nothing while loading made "still fetching" pixel-identical to "has no
  // subsidiaries", which is genuinely misleading — it fooled us twice while
  // testing this panel. Rendering it immediately would instead flash a
  // placeholder on the majority of customers, who have no subsidiaries and
  // resolve in milliseconds.
  if (query.isLoading) {
    return loadingSettled ? (
      <SubsidiariesSection>
        <Skeleton className="h-4 w-28" />
        <Skeleton className="h-16 w-full" />
      </SubsidiariesSection>
    ) : null;
  }

  const rows = query.data?.data ?? [];
  // `meta?.` — the optional chain has to cover `meta`, not just `data`, or a
  // response missing it takes down the whole Details tab with a TypeError.
  const total = query.data?.meta?.total ?? rows.length;

  // An empty page that ISN'T the first one means the page index outran the
  // data — subsidiaries were detached while we sat here, or the reset above
  // hasn't landed yet. Without this the panel renders an empty table under
  // "Showing 26–25 of 20", which reads as corrupted rather than stale.
  const strandedPastEnd = rows.length === 0 && page > 0;
  if (rows.length === 0) {
    if (strandedPastEnd) setPage(0);
    return null;
  }

  const firstShown = page * PAGE_SIZE + 1;
  const lastShown = page * PAGE_SIZE + rows.length;
  const hasMore = lastShown < total;

  return (
    <SubsidiariesSection>
      <h3 className="text-sm font-medium text-muted-foreground">
        Subsidiaries
        <span className="ml-1 text-xs">{total}</span>
      </h3>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>QB ID</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((c) => (
              <TableRow key={c.id}>
                <TableCell>
                  <Link
                    to="/customers/$id"
                    params={{ id: c.id }}
                    className="text-primary underline-offset-4 hover:underline"
                  >
                    {c.name}
                  </Link>
                </TableCell>
                <TableCell className="text-muted-foreground">{c.qbId}</TableCell>
                <TableCell>
                  <StatusBadge status={c.status} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {/* Paging rather than a bare "showing the first N" notice: a parent with
          more subsidiaries than one page had no way to reach the rest from any
          screen, since the list has no parent filter chip to fall back to. */}
      {total > PAGE_SIZE ? (
        <div className="flex items-center justify-between">
          <p className="text-xs text-muted-foreground">
            Showing {firstShown}–{lastShown} of {total}.
          </p>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              Previous
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={!hasMore}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      ) : null}
    </SubsidiariesSection>
  );
}
